import {UseFormReturn} from 'react-hook-form'
import {useTranslation} from '@/translations/i18n'
import {MailerFormValidationSchema} from '@/features/mailer/types'
import {useGetDistricts} from '@/services/useGetDistricts'
import {useGetUserSpecializations} from '@/features/users/queries/useGetUserSpecializations'

export interface ActiveFilter {
    key: string
    label: string
    onRemove: () => void
}

export const useActiveMailerFilters = (
    form: UseFormReturn<MailerFormValidationSchema>
): {patient: ActiveFilter[]; consultant: ActiveFilter[]; clearAll: () => void} => {
    const {t} = useTranslation()
    const v = form.watch()
    const {setValue} = form
    const districtsQuery = useGetDistricts({responseType: 'small', limit: 100})
    const specializationsQuery = useGetUserSpecializations({limit: 100})

    const aslName = v.patientResidenceAslDistrictId
        ? districtsQuery.remappedData.find(d => d.id === v.patientResidenceAslDistrictId)
        : undefined
    const specName = v.consultantSpecializationId
        ? specializationsQuery.remappedData.find(s => s.id === v.consultantSpecializationId)?.name
        : undefined

    const serviceTypeLabel = (value: string): string => {
        switch (value) {
            case 'adults':
                return t('mailer:service_type_adults')
            case 'evolutionary_age':
                return t('mailer:service_type_evolutionary_age')
            case 'evolutionary_age_adults':
                return t('mailer:service_type_evolutionary_age_adults')
            default:
                return value
        }
    }
    const genderLabel = (value: string): string => {
        switch (value) {
            case 'male':
                return t('mailer:gender_male')
            case 'female':
                return t('mailer:gender_female')
            default:
                return value
        }
    }
    const statusLabel = (value: string): string => {
        switch (value) {
            case 'operative':
                return t('mailer:status_operative')
            case 'candidate':
                return t('mailer:status_candidate')
            case 'suspended':
                return t('mailer:status_suspended')
            case 'absent':
                return t('mailer:status_absent')
            case 'terminated':
                return t('mailer:status_terminated')
            default:
                return value
        }
    }
    const contractLabel = (value: string): string => {
        switch (value) {
            case 'consultant':
                return t('mailer:contract_type_consultant')
            case 'flat_rate':
                return t('mailer:contract_type_flat_rate')
            case 'employee':
                return t('mailer:contract_type_employee')
            default:
                return value
        }
    }
    const workSectorLabel = (value: string): string => {
        switch (value) {
            case 'domiciliary':
                return t('mailer:work_sector_domiciliary')
            case 'outpatient':
                return t('mailer:work_sector_outpatient')
            case 'outpatient_domiciliary':
                return t('mailer:work_sector_outpatient_domiciliary')
            default:
                return value
        }
    }

    const patient: ActiveFilter[] = []
    if (v.includePatients) {
        if (v.patientServiceType) {
            patient.push({
                key: 'patientServiceType',
                label: `${t('mailer:service_type')}: ${serviceTypeLabel(v.patientServiceType)}`,
                onRemove: () => setValue('patientServiceType', undefined)
            })
        }
        if (v.patientMinAge) {
            patient.push({
                key: 'patientMinAge',
                label: t('mailer:filter_age_min', {value: v.patientMinAge}),
                onRemove: () => setValue('patientMinAge', undefined)
            })
        }
        if (v.patientMaxAge) {
            patient.push({
                key: 'patientMaxAge',
                label: t('mailer:filter_age_max', {value: v.patientMaxAge}),
                onRemove: () => setValue('patientMaxAge', undefined)
            })
        }
        if (v.patientGender) {
            patient.push({
                key: 'patientGender',
                label: `${t('mailer:gender')}: ${genderLabel(v.patientGender)}`,
                onRemove: () => setValue('patientGender', undefined)
            })
        }
        if (v.patientOutsideRegion) {
            patient.push({
                key: 'patientOutsideRegion',
                label: t('mailer:outside_region'),
                onRemove: () => setValue('patientOutsideRegion', false)
            })
        }
        if (v.patientNeuromuscularDiagnosisId) {
            patient.push({
                key: 'patientNeuromuscularDiagnosisId',
                label: t('mailer:disease_type'),
                onRemove: () => setValue('patientNeuromuscularDiagnosisId', undefined)
            })
        }
        if (v.patientResidenceAslDistrictId) {
            const aslLabel = aslName ? `${aslName.aslName} - ${aslName.name}` : t('mailer:asl_district')
            patient.push({
                key: 'patientResidenceAslDistrictId',
                label: `${t('mailer:asl_district')}: ${aslLabel}`,
                onRemove: () => setValue('patientResidenceAslDistrictId', undefined)
            })
        }
    }

    const consultant: ActiveFilter[] = []
    if (v.includeConsultants) {
        if (v.consultantStatus) {
            consultant.push({
                key: 'consultantStatus',
                label: `${t('mailer:status')}: ${statusLabel(v.consultantStatus)}`,
                onRemove: () => setValue('consultantStatus', undefined)
            })
        }
        if (v.consultantContractType) {
            consultant.push({
                key: 'consultantContractType',
                label: `${t('mailer:contract_type')}: ${contractLabel(v.consultantContractType)}`,
                onRemove: () => setValue('consultantContractType', undefined)
            })
        }
        if (v.consultantWorkSector) {
            consultant.push({
                key: 'consultantWorkSector',
                label: `${t('mailer:work_sector')}: ${workSectorLabel(v.consultantWorkSector)}`,
                onRemove: () => setValue('consultantWorkSector', undefined)
            })
        }
        if (v.consultantSpecializationId) {
            consultant.push({
                key: 'consultantSpecializationId',
                label: specName ? `${t('mailer:specialization')}: ${specName}` : t('mailer:specialization'),
                onRemove: () => setValue('consultantSpecializationId', undefined)
            })
        }
    }

    const clearAll = () => {
        setValue('patientServiceType', undefined)
        setValue('patientMinAge', undefined)
        setValue('patientMaxAge', undefined)
        setValue('patientGender', undefined)
        setValue('patientOutsideRegion', false)
        setValue('patientNeuromuscularDiagnosisId', undefined)
        setValue('patientResidenceAslDistrictId', undefined)
        setValue('consultantStatus', undefined)
        setValue('consultantContractType', undefined)
        setValue('consultantWorkSector', undefined)
        setValue('consultantSpecializationId', undefined)
    }

    return {patient, consultant, clearAll}
}
